import AppComponent from '../base/app-component.ts';
import { formatNumero } from '../lib/utils.ts';
import type { ChartJsInstance } from '../types/chartjs-browser.ts';

interface SeriePunto {
  periodo: string;
  count: number;
}

interface SerieStatistica {
  sigla: 'FILCA' | 'FILLEA' | 'FENEAL' | 'NON ISCRITTI';
  nome: string;
  color: string;
  punti: SeriePunto[];
}

interface SerieResponse {
  nomeTabella: string;
  periodi: string[];
  serie: SerieStatistica[];
  latestPeriodo: string;
  latestSnapshot: Array<{ sigla: string; nome: string; color: string; count: number }>;
  maxCount: number;
}

const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

export class GraficoStatistiche extends AppComponent {
  static override componentName = 'wc-grafico-statistiche';

  protected override get templateId() { return 'grafico-statistiche'; }

  private chartInstance: ChartJsInstance | null = null;

  protected override afterRender(): void {
    void this.carica();
  }

  protected override onDisconnected(): void {
    this.destroyChart();
  }

  private async carica(): Promise<void> {
    const nomeTabella = this.attributeString('nome-tabella');
    if (!nomeTabella) {
      this.showError('Attributo nome-tabella mancante');
      return;
    }

    this.showLoading();

    const result = await this.api.statistiche.serie(nomeTabella);
    if (!result.ok) {
      this.showError(result.error);
      return;
    }

    this.renderGrafico(result.data);
  }

  private showLoading(): void {
    this.destroyChart();
    const chart = this.$('[data-chart-status]');
    const legend = this.$('[data-legend]');
    if (chart) chart.innerHTML = '<div class="text-center text-muted py-5"><div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>Caricamento grafico...</div>';
    if (legend) legend.innerHTML = '';
  }

  private showError(message: string): void {
    this.destroyChart();
    const chart = this.$('[data-chart-status]');
    const legend = this.$('[data-legend]');
    if (chart) chart.innerHTML = `<div class="alert alert-danger mb-0">${message}</div>`;
    if (legend) legend.innerHTML = '';
  }

  private renderGrafico(data: SerieResponse): void {
    const chartStatus = this.$('[data-chart-status]');
    const canvas = this.$('[data-chart-canvas]') as HTMLCanvasElement | null;
    const legend = this.$('[data-legend]');
    if (!chartStatus || !canvas || !legend) return;

    if (data.periodi.length === 0 || data.serie.length === 0) {
      this.destroyChart();
      chartStatus.innerHTML = '<div class="alert alert-info mb-0">Nessun dato disponibile per il grafico.</div>';
      legend.innerHTML = '';
      return;
    }
    chartStatus.innerHTML = '';
    this.mountChart(canvas, data);

    legend.innerHTML = `
      <div class="row g-2">
        ${data.latestSnapshot.map(item => `
          <div class="col-12 col-sm-6 col-xl-3">
            <div class="border rounded p-3 h-100">
              <div class="d-flex align-items-center gap-2 mb-2">
                <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${item.color};"></span>
                <span class="small text-muted">${item.nome}</span>
              </div>
              <div class="fs-4 fw-semibold">${formatNumero(item.count)}</div>
              <div class="small text-muted">${data.latestPeriodo ? formatPeriodo(data.latestPeriodo) : ''}</div>
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  private mountChart(canvas: HTMLCanvasElement, data: SerieResponse): void {
    const ChartCtor = window.Chart;
    if (!ChartCtor) {
      this.showError('Chart.js non disponibile');
      return;
    }

    this.destroyChart();

    this.chartInstance = new ChartCtor(canvas, {
      type: 'line',
      data: {
        labels: data.periodi.map(formatPeriodo),
        datasets: data.serie.map(serie => ({
          label: serie.nome,
          data: serie.punti.map(punto => punto.count),
          borderColor: serie.color,
          backgroundColor: serie.color,
          borderWidth: 3,
          tension: 0.25,
          pointRadius: 4,
          pointHoverRadius: 6,
          fill: false,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label(context: { dataset: { label?: string }; parsed: { y: number } }) {
                const label = context.dataset.label ?? '';
                return `${label}: ${formatNumero(context.parsed.y)}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax: this.normalizeMax(data.maxCount),
            ticks: {
              callback(value: unknown) {
                return formatNumero(value);
              },
            },
          },
          x: {
            ticks: {
              maxRotation: 0,
              autoSkip: true,
            },
          },
        },
      },
    });
  }

  private destroyChart(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }

  private normalizeMax(value: number): number {
    if (value <= 0) return 10;
    const magnitude = 10 ** Math.floor(Math.log10(value));
    return Math.ceil(value / magnitude) * magnitude;
  }
}

function formatPeriodo(periodo: string): string {
  const anno = periodo.slice(0, 4);
  const mese = Number(periodo.slice(4, 6)) - 1;
  return `${MESI[mese] ?? periodo.slice(4, 6)} ${anno}`;
}
